import { DineInTableState } from "./dineInBoardBuilder";
import { DINE_IN_ITEM_STATUS } from "../../../../../../services/helpers/DineInSummaryHelper";

/**
 * Display metadata for each table state, shared by the tiles and the legend so
 * the two can never drift into calling the same colour different things.
 *
 * `modifier` is a CSS class suffix rather than a colour value on purpose: the
 * actual colours live in styles/DineInBoardStates.css as a
 * `--dine-in-state-color` custom property per state, which both the tile
 * border and the legend dot read. One definition, two consumers, no hex codes
 * duplicated into TypeScript.
 */
export const DINE_IN_TABLE_STATE_META: Record<
  DineInTableState,
  { label: string; modifier: string }
> = {
  free: { label: "Free", modifier: "free" },
  active: { label: "Active", modifier: "active" },
  needsAction: { label: "Needs action", modifier: "needs-action" },
  billRequested: { label: "Bill requested", modifier: "bill-requested" },
};

/**
 * Legend order, left to right. Deliberately excludes billRequested: the board
 * never produces that state (no backend signal exists for it), and a legend
 * entry for something that can't appear just makes staff hunt for a colour
 * that will never show up. Add it here the moment the API can report it.
 */
export const DINE_IN_LEGEND_STATES: DineInTableState[] = ["free", "active", "needsAction"];

/**
 * Display metadata for a single ITEM's kitchen status.
 *
 * Wire values and the words staff read are kept separate on purpose, even
 * where they currently match: the backend's ORDERED reads as "Requested" here,
 * while SERVED reads as "Served". Keeping the translation in one place means
 * neither leaks into the other — renaming a label never touches a comparison,
 * and a backend rename never touches a component.
 *
 * Keyed by the NORMALISED (upper-cased) status — always look up through
 * normalizeDineInItemStatus, never with a raw response value.
 */
export const DINE_IN_ITEM_STATUS_META: Record<
  string,
  { label: string; modifier: string }
> = {
  [DINE_IN_ITEM_STATUS.ORDERED]: { label: "Requested", modifier: "ordered" },
  [DINE_IN_ITEM_STATUS.ACCEPTED]: { label: "Accepted", modifier: "accepted" },
  [DINE_IN_ITEM_STATUS.REJECTED]: { label: "Rejected", modifier: "rejected" },
  [DINE_IN_ITEM_STATUS.SERVED]: { label: "Served", modifier: "served" },
};

/**
 * Falls back to showing the raw status rather than hiding it. An unrecognised
 * value means the backend knows something this build doesn't — surfacing it
 * (unstyled, unactionable) tells whoever is looking that something new exists,
 * where a blank cell would just look like a bug.
 */
export function itemStatusMeta(normalizedStatus: string): {
  label: string;
  modifier: string;
} {
  return (
    DINE_IN_ITEM_STATUS_META[normalizedStatus] ?? {
      label: normalizedStatus || "Unknown",
      modifier: "unknown",
    }
  );
}
